/* GASSISA
=========================================================================================
* Descripción: Menú de acciones administrativas para apartados
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Reimpresión de abono seleccionado
*      3       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Reimpresión de acuse de entrega
*      4       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Validación y estilo estándar de reimpresiones
* ---------------------------------------------------------------------------------------
*/

import { Box, ClickAwayListener, Grid, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PrintIcon from '@mui/icons-material/Print';
import { useVentas } from "../../context/Ventas/VentasContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useDoubleClickPrevention } from '../../hooks/useDoubleClickPrevention';

export default function MnuOtherActionsApartados({ open, onClose }) {
    const {
        abonoApartadoSeleccionado,
        dataEstadoCuentaApartado,
        handleMantenimientoCuentasApartados,
        handleReimprimirAbonoApartado,
        handleImprimirTicketAcuseEntrega,
        statusWaiting
    } = useVentas();
    const { handleOpenAlert } = useLayout();
    const { isProcessing, executeWithProtection } = useDoubleClickPrevention();
    const isDisabled = statusWaiting || isProcessing;
    const hasAbonoSeleccionado = !!abonoApartadoSeleccionado?.iabono;
    const apartadoSeleccionado = dataEstadoCuentaApartado?.apartados?.[0] || {};
    const statusApartado = (apartadoSeleccionado?.status || "").toString().trim().toUpperCase();
    const hasAcuseDisponible = !!apartadoSeleccionado?.folioapartado && (statusApartado === "TR" || statusApartado === "ENTREGADO");
    const getActionColor = (disabled, hoverColor = '#2ecc40') => ({
        color: disabled ? undefined : 'white',
        '& .MuiTypography-root': {
            color: disabled ? undefined : 'white',
            fontWeight: 'bold',
        },
        '&:hover': {
            color: disabled ? undefined : hoverColor,
            '& .MuiTypography-root': {
                color: disabled ? undefined : hoverColor,
            },
        },
    });

    if (!open) return null;

    const handleMantenimiento = () => {
        const confirmado = window.confirm(
            "Se revisarán apartados vencidos por 30 días hábiles y se devolverá la mercancía de APARTADOS al almacén dominio. ¿Desea continuar?"
        );

        if (!confirmado) return;

        executeWithProtection(async () => {
            await handleMantenimientoCuentasApartados();
            onClose();
        });
    };

    const handleReimprimirAbono = () => {
        if (!hasAbonoSeleccionado) {
            handleOpenAlert("Seleccione un abono del detalle para reimprimir");
            return;
        }

        const abonoData = {
            iabono: abonoApartadoSeleccionado.iabono,
            folioapartado: abonoApartadoSeleccionado.folioapartado,
            idpartida: abonoApartadoSeleccionado.idpartida,
            montoabono: abonoApartadoSeleccionado.montoabono,
            fechaabono: abonoApartadoSeleccionado.fechaabono,
            clavearticulo: abonoApartadoSeleccionado.clavearticulo,
            descripcion: abonoApartadoSeleccionado.descripcion,
            medida: abonoApartadoSeleccionado.medida,
            cantidad: abonoApartadoSeleccionado.cantidad,
            costoarticulo: abonoApartadoSeleccionado.costocliente,
            nombrecliente: dataEstadoCuentaApartado?.nombrecliente || ""
        };

        executeWithProtection(async () => {
            await handleReimprimirAbonoApartado(abonoData);
            onClose();
        });
    };

    const handleReimprimirAcuse = () => {
        if (!hasAcuseDisponible) {
            handleOpenAlert("Seleccione un apartado terminado para reimprimir el acuse");
            return;
        }

        const acuseData = {
            ...apartadoSeleccionado,
            dTotalAbonado: dataEstadoCuentaApartado?.totalabonado || apartadoSeleccionado?.costoarticulo || 0,
            totalabonado: dataEstadoCuentaApartado?.totalabonado || apartadoSeleccionado?.costoarticulo || 0,
            sNombreCliente: dataEstadoCuentaApartado?.nombrecliente || apartadoSeleccionado?.nombrecliente || "",
            nombrecliente: dataEstadoCuentaApartado?.nombrecliente || apartadoSeleccionado?.nombrecliente || "",
            sDescripcionArticulo: apartadoSeleccionado?.descripcion || "",
            descripcionarticulo: apartadoSeleccionado?.descripcion || "",
            sClaveArticulo: apartadoSeleccionado?.clavearticulo || "",
            clavearticulo: apartadoSeleccionado?.clavearticulo || "",
            dMedida: apartadoSeleccionado?.medida || 0,
            medida: apartadoSeleccionado?.medida || 0,
            dCantidadArticulo: apartadoSeleccionado?.cantidadarticulo || 0,
            cantidadarticulo: apartadoSeleccionado?.cantidadarticulo || 0,
            dCostoArticulo: apartadoSeleccionado?.costoarticulo || 0,
            costoarticulo: apartadoSeleccionado?.costoarticulo || 0,
        };

        executeWithProtection(async () => {
            await handleImprimirTicketAcuseEntrega(acuseData);
            onClose();
        });
    };

    return (
        <ClickAwayListener onClickAway={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '230px',
                    height: '100%',
                    backgroundColor: '#191970',
                    boxShadow: 3,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    zIndex: 1200,
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton
                        onClick={onClose}
                        disabled={isDisabled}
                        sx={{ color: isDisabled ? undefined : 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Grid container spacing={2}>
                    <Grid item>
                        <IconButton
                            onClick={handleMantenimiento}
                            disabled={isDisabled}
                            sx={getActionColor(isDisabled)}
                        >
                            <ManageAccountsIcon />
                            <Typography
                                variant="body2"
                            >
                                Mantenimiento a cuentas
                            </Typography>
                        </IconButton>
                    </Grid>
                    <Grid item>
                        <IconButton
                            onClick={handleReimprimirAbono}
                            disabled={isDisabled || !hasAbonoSeleccionado}
                            sx={getActionColor(isDisabled || !hasAbonoSeleccionado)}
                        >
                            <PrintIcon />
                            <Typography
                                variant="body2"
                            >
                                Reimprimir abono
                            </Typography>
                        </IconButton>
                    </Grid>
                    <Grid item>
                        <IconButton
                            onClick={handleReimprimirAcuse}
                            disabled={isDisabled || !hasAcuseDisponible}
                            sx={getActionColor(isDisabled || !hasAcuseDisponible)}
                        >
                            <PrintIcon />
                            <Typography
                                variant="body2"
                            >
                                Reimprimir acuse
                            </Typography>
                        </IconButton>
                    </Grid>
                </Grid>
            </Box>
        </ClickAwayListener>
    );
}
